import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import NotificationBell from '../components/NotificationBell';
import { 
  Activity, LayoutDashboard, Stethoscope, Calendar, 
  MessageSquare, LogOut, Menu, X, Settings, Users
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function NurseLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const navigation = [
    { name: 'Dashboard', href: '/nurse', icon: LayoutDashboard },
    { name: 'Triaje', href: '/nurse/triaje', icon: Stethoscope },
    { name: 'Pacientes', href: '/nurse/patients', icon: Users },
    { name: 'Citas', href: '/nurse/appointments', icon: Calendar },
  ];

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } bg-teal-900 text-white transition-all duration-300 overflow-hidden flex flex-col`}
        data-testid="sidebar"
      >
        <div className="p-6 flex items-center gap-3 border-b border-teal-800">
          <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center">
            <Activity className="w-6 h-6" />
          </div>
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              ClinIA Perú
            </h2>
            <p className="text-xs text-teal-300">Enfermería</p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-md text-sm ${
                  isActive ? 'active bg-teal-800' : 'hover:bg-teal-800'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-teal-800">
          <Button
            variant="ghost"
            className="w-full justify-start text-white hover:bg-teal-800 hover:text-white"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="sticky top-0 z-10 backdrop-blur-md bg-white/80 border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-button"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            <div className="flex items-center gap-4">
              <NotificationBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-button">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-teal-600 text-white">
                        {getInitials(user?.full_name || 'EN')}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">{user?.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Configuración
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6" data-testid="main-content">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
